import { apiDelete, apiGet, apiPost, apiPut } from './api';
import type { NotificationPreferenceResponse } from '../types/notification';

/**
 * Web Push / PWA device management. All server calls are owner-scoped (the
 * backend derives the user from the session), and the VAPID private key never
 * reaches the browser — only the public key from `/push/config` is used.
 *
 * Local binding is kept deliberately small: just the server subscription id and
 * the owning user id. The raw endpoint and keys are never stored in localStorage.
 * Permission is only ever requested from an explicit user gesture
 * (`enableDevicePush` called by a button), never on mount.
 */

export const NOTIFICATION_SW_PATH = '/notification-sw.js';
export const NOTIFICATION_SW_SCOPE = '/';

const BINDING_KEY = 'gta.push.binding';

export type PushReason =
  | 'ok'
  | 'unsupported'
  | 'ios-needs-install'
  | 'permission-denied'
  | 'permission-default'
  | 'not-configured'
  | 'in-app-browser';

export interface PushCapabilities {
  supported: boolean;
  permission: NotificationPermission | 'unsupported';
  installed: boolean;
  reason: PushReason;
}

export interface PushBinding {
  subscriptionId: string;
  userId: number;
}

export interface PushDevice {
  id: string;
  deviceLabel: string | null;
  createdAt: string;
  lastSeenAt: string;
  revoked: boolean;
  /** True for the subscription this browser is currently subscribed as. */
  isCurrent?: boolean;
}

export interface PushConfig {
  enabled: boolean;
  publicKey: string;
}

/** Error carrying a machine-readable reason for the enable flow. */
export class PushEnableError extends Error {
  readonly reason: PushReason;

  constructor(reason: PushReason) {
    super(reason);
    this.name = 'PushEnableError';
    this.reason = reason;
  }
}

// ── server API ──────────────────────────────────────────────────────────────

export function getPushConfig(): Promise<PushConfig> {
  return apiGet<PushConfig>('/api/notifications/push/config');
}

export function listPushDevices(): Promise<PushDevice[]> {
  return apiGet<PushDevice[]>('/api/notifications/push/subscriptions');
}

export function registerPushSubscription(body: {
  endpoint: string;
  keys: { p256dh: string; auth: string };
  deviceLabel?: string;
}): Promise<{ id: string; bindingVersion: number }> {
  return apiPost<{ id: string; bindingVersion: number }>(
    '/api/notifications/push/subscriptions',
    body,
  );
}

export function revokePushSubscription(id: string): Promise<void> {
  return apiDelete<void>(`/api/notifications/push/subscriptions/${id}`);
}

export function sendTestPush(subscriptionId: string): Promise<{ queued: boolean }> {
  return apiPost<{ queued: boolean }>('/api/notifications/push/test', { subscriptionId });
}

// ── capability detection ────────────────────────────────────────────────────

export function getPushCapabilities(): PushCapabilities {
  const installed = isStandalone();
  const notificationApi = typeof Notification !== 'undefined' ? Notification : null;
  const serviceWorkerSupported =
    typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
  const pushManagerSupported = typeof window !== 'undefined' && 'PushManager' in window;

  if (!notificationApi || !serviceWorkerSupported || !pushManagerSupported) {
    return {
      supported: false,
      permission: 'unsupported',
      installed,
      reason: isIos() && !installed ? 'ios-needs-install' : 'unsupported',
    };
  }

  const permission = notificationApi.permission;
  if (permission === 'denied') {
    return { supported: true, permission, installed, reason: 'permission-denied' };
  }
  if (permission === 'default') {
    // iOS Safari only exposes push once the app is added to the Home Screen.
    const reason: PushReason = isIos() && !installed ? 'ios-needs-install' : 'permission-default';
    return { supported: true, permission, installed, reason };
  }
  return { supported: true, permission, installed, reason: 'ok' };
}

function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const mediaStandalone =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches;
  const navStandalone =
    (navigator as unknown as { standalone?: boolean }).standalone === true;
  return mediaStandalone || navStandalone;
}

// ── service worker registration ─────────────────────────────────────────────

/** Idempotent registration; safe to call on every authenticated mount. */
export async function registerNotificationServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return null;
  }
  try {
    return await navigator.serviceWorker.register(NOTIFICATION_SW_PATH, {
      scope: NOTIFICATION_SW_SCOPE,
      updateViaCache: 'none',
    });
  } catch {
    return null;
  }
}

export async function getPushRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return null;
  }
  return (await navigator.serviceWorker.getRegistration(NOTIFICATION_SW_SCOPE)) ?? null;
}

// ── local binding ───────────────────────────────────────────────────────────

export function readLocalBinding(): PushBinding | null {
  try {
    const raw = localStorage.getItem(BINDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PushBinding;
    if (typeof parsed?.subscriptionId === 'string' && typeof parsed?.userId === 'number') {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

function writeLocalBinding(binding: PushBinding): void {
  try {
    localStorage.setItem(BINDING_KEY, JSON.stringify(binding));
  } catch {
    // Storage unavailable (private mode): binding is simply not cached.
  }
}

export function clearLocalBinding(): void {
  try {
    localStorage.removeItem(BINDING_KEY);
  } catch {
    // ignore
  }
}

// ── enable / disable ────────────────────────────────────────────────────────

/**
 * Enables push on this device. MUST be called from a user gesture: it is the
 * only place that requests notification permission. On any server failure the
 * browser subscription is rolled back so a retry does not create duplicates.
 *
 * On success the global `pushEnabled` preference is turned on as well. A device
 * registration alone does not make push work — the delivery policy gates every
 * push on `pushEnabled` — so a user who explicitly enabled a device would
 * otherwise receive nothing. The opt-in is deliberately NOT rolled back if that
 * preference write fails: the device is registered, and the settings panel warns
 * that the preference is off.
 */
export async function enableDevicePush(options: {
  userId: number;
  deviceLabel?: string;
}): Promise<{ device: PushDevice; subscriptionId: string }> {
  const caps = getPushCapabilities();
  if (!caps.supported) throw new PushEnableError(caps.reason);
  if (caps.reason === 'ios-needs-install') throw new PushEnableError('ios-needs-install');

  const permission =
    Notification.permission === 'granted'
      ? 'granted'
      : await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new PushEnableError(
      permission === 'denied' ? 'permission-denied' : 'permission-default',
    );
  }

  const config = await getPushConfig();
  if (!config.enabled || !config.publicKey) {
    throw new PushEnableError('not-configured');
  }

  const registration = await registerNotificationServiceWorker();
  if (!registration) throw new PushEnableError('unsupported');

  const existing = await registration.pushManager.getSubscription();
  const subscription =
    existing ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(config.publicKey) as BufferSource,
    }));

  try {
    const result = await registerPushSubscription({
      endpoint: subscription.endpoint,
      keys: serializeKeys(subscription),
      deviceLabel: options.deviceLabel,
    });
    writeLocalBinding({ subscriptionId: result.id, userId: options.userId });
    await enablePushPreference();
    return {
      subscriptionId: result.id,
      device: {
        id: result.id,
        deviceLabel: options.deviceLabel ?? null,
        createdAt: new Date().toISOString(),
        lastSeenAt: new Date().toISOString(),
        revoked: false,
      },
    };
  } catch (error) {
    // Roll back the browser side so a retry does not accumulate subscriptions.
    await subscription.unsubscribe().catch(() => undefined);
    throw error;
  }
}

/**
 * Revokes a device, server first. Only the device the browser is actually
 * subscribed as may have its local subscription torn down: revoking a phone from
 * a laptop must leave the laptop's own subscription intact. A failed server
 * revoke keeps the local subscription and throws, so the UI can stay truthful
 * instead of claiming a device that is still live.
 */
export async function disableDevicePush(subscriptionId: string): Promise<void> {
  await revokePushSubscription(subscriptionId);
  if (readLocalBinding()?.subscriptionId !== subscriptionId) {
    const devices = await listPushDevices().catch(() => null);
    if (!devices?.some((device) => device.id === subscriptionId && device.isCurrent)) return;
  }
  await unsubscribeLocal();
  clearLocalBinding();
}

/**
 * Best-effort cleanup before logout: revoke the server binding, drop the local
 * subscription and cached OS notifications. Bounded by a timeout so an offline
 * network cannot stall logout — the caller proceeds regardless.
 */
export async function cleanupPushBeforeLogout(timeoutMs = 1000): Promise<void> {
  const binding = readLocalBinding();
  const revoke = (async () => {
    if (binding) {
      await revokePushSubscription(binding.subscriptionId).catch(() => undefined);
    }
    await unsubscribeLocal();
    await clearCachedNotifications();
    clearLocalBinding();
  })();

  await Promise.race([
    revoke,
    new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
  ]);
}

/**
 * Reconciles after a `pushsubscriptionchange`: if the browser rotated the
 * subscription while the app was closed, re-register it. No permission prompt.
 */
export async function reconcilePushSubscription(userId: number): Promise<void> {
  const caps = getPushCapabilities();
  if (!caps.supported || caps.permission !== 'granted') return;
  const registration = await getPushRegistration();
  if (!registration) return;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;
  try {
    const result = await registerPushSubscription({
      endpoint: subscription.endpoint,
      keys: serializeKeys(subscription),
    });
    writeLocalBinding({ subscriptionId: result.id, userId });
  } catch {
    // Reconcile is opportunistic; a later app-open retries.
  }
}

/**
 * Tells the service worker which user this page belongs to, so it can show a
 * generic message when a push for a different account arrives. The binding stays
 * in the worker's memory only and is cleared on logout.
 */
export async function notifyBindingToServiceWorker(userId: number | null): Promise<void> {
  if (typeof navigator === 'undefined' || !navigator.serviceWorker) return;
  const registration = await navigator.serviceWorker.ready.catch(() => null);
  registration?.active?.postMessage(
    userId == null ? { type: 'clear-binding' } : { type: 'set-binding', userId },
  );
}

async function unsubscribeLocal(): Promise<void> {
  const registration = await getPushRegistration();
  if (!registration) return;
  const subscription = await registration.pushManager.getSubscription();
  await subscription?.unsubscribe().catch(() => undefined);
}

/**
 * Persists `pushEnabled = true` on the current preference document. Reads the
 * current version first (optimistic concurrency) and sends the whole config back
 * as a merge, so a concurrent edit is never clobbered silently. Best effort: a
 * failure is logged and swallowed by the caller, because the device registration
 * itself already succeeded.
 */
async function enablePushPreference(): Promise<void> {
  try {
    const current = await apiGet<NotificationPreferenceResponse>(
      '/api/notifications/preferences',
    );
    if (current?.config?.pushEnabled) return;
    await apiPut<NotificationPreferenceResponse>('/api/notifications/preferences', {
      version: current?.version ?? 0,
      config: { ...current.config, pushEnabled: true },
    });
  } catch {
    // The settings panel surfaces the still-off preference and lets the user
    // turn it on explicitly.
  }
}

async function clearCachedNotifications(): Promise<void> {
  const registration = await getPushRegistration();
  if (!registration) return;
  const shown = await registration.getNotifications?.().catch(() => []);
  shown?.forEach((n) => n.close());
}

function serializeKeys(subscription: PushSubscription): { p256dh: string; auth: string } {
  const json = subscription.toJSON?.() as
    | { keys?: { p256dh?: string; auth?: string } }
    | undefined;
  return {
    p256dh: json?.keys?.p256dh ?? '',
    auth: json?.keys?.auth ?? '',
  };
}

/** VAPID public key (base64url) → Uint8Array for `applicationServerKey`. */
export function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normalized);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
}
